'use client'

import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import styles from './styles.module.css'

interface SuccessModalProps {
  isOpen: boolean
  onClose: () => void
  manageScroll?: boolean
}

const SuccessModal: React.FC<SuccessModalProps> = ({ isOpen, onClose, manageScroll = true }) => {
  const [isVisible, setIsVisible] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  // Создаем контейнер для портала один раз
  useEffect(() => {
    let portalContainer = document.getElementById('success-modal-portal')
    if (!portalContainer) {
      portalContainer = document.createElement('div')
      portalContainer.id = 'success-modal-portal'
      portalContainer.style.position = 'fixed'
      portalContainer.style.top = '0'
      portalContainer.style.left = '0'
      portalContainer.style.zIndex = '10001'
      portalContainer.style.pointerEvents = 'auto'
      
      document.body.insertBefore(portalContainer, document.body.firstChild)
    }

    // Cleanup on unmount
    return () => {
      const existingContainer = document.getElementById('success-modal-portal')
      if (existingContainer) {
        existingContainer.remove()
      }
    }
  }, [])

  // Управляем анимацией
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true)
      // Небольшая задержка для плавного появления
      setTimeout(() => {
        setIsAnimating(true)
      }, 10)
    } else {
      setIsAnimating(false)
      // Задержка перед скрытием для завершения анимации
      const timer = setTimeout(() => {
        setIsVisible(false)
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  // Обработчик ESC клавиши для SuccessModal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  if (!isVisible) return null

  const portalContainer = document.getElementById('success-modal-portal')
  if (!portalContainer) return null

  return createPortal(
    <>
      {/* Backdrop with blur */}
      <div 
        className={`${styles.backdrop} ${isAnimating ? styles.show : ''}`}
        onClick={onClose}
      />
      
      {/* Success Modal */}
      <div className={`${styles.modalContainer} ${isAnimating ? styles.show : ''}`}>
        <div 
          className={`${styles.successModalContent} ${isAnimating ? styles.show : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <div style={{ position: 'absolute', top: '20px', right: '20px' }}>
            <button 
              onClick={onClose}
              style={{ 
                transition: 'opacity 0.2s ease',
                border: 'none',
                background: 'none',
                padding: 0,
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 20C0 8.95431 8.95431 0 20 0C31.0457 0 40 8.95431 40 20C40 31.0457 31.0457 40 20 40C8.95431 40 0 31.0457 0 20Z" fill="#F3F2F4"/>
                <path d="M24.6663 16.2734L23.7263 15.3334L19.9997 19.06L16.273 15.3334L15.333 16.2734L19.0597 20L15.333 23.7267L16.273 24.6667L19.9997 20.94L23.7263 24.6667L24.6663 23.7267L20.9397 20L24.6663 16.2734Z" fill="#151515"/>
              </svg>
            </button>
          </div>

          {/* Success Title */}
          <h2 className={styles.successTitle}>
            Успешно!
          </h2>

          {/* Success Message */}
          <div className={styles.successMessage}>
            <p>Ваша заявка успешно отправлена.</p>
            <p>Наш менеджер в ближайшее время свяжется с вами</p>
          </div>
        </div>
      </div>
    </>,
    portalContainer
  )
}

SuccessModal.displayName = 'SuccessModal'

export default SuccessModal
